import React, { useState } from 'react';
import { FaPlus, FaCalendarAlt, FaList, FaChartBar, FaCog, FaWallet, FaFileAlt, FaUniversity, FaPiggyBank } from 'react-icons/fa';
import { FinanceInputModal } from '../components/finance/FinanceInputModal';
import { FinanceList } from '../components/finance/FinanceList';
import { MonthlyReport } from '../components/finance/MonthlyReport';
import { AnnualFinanceView } from '../components/finance/AnnualFinanceView';
import { BankBalanceManager } from '../components/finance/BankBalanceManager';
import { CarryoverFundsManager } from '../components/admin/CarryoverFundsManager';
import { FinanceOverview } from '../components/finance/FinanceOverview';

type TabType = 'input' | 'list' | 'report' | 'manage';

interface TabConfig {
  id: TabType;
  label: string;
  icon: React.ReactNode;
  description: string;
  gradient: string;
}

const tabs: TabConfig[] = [
  {
    id: 'input',
    label: '입력',
    icon: <FaPlus className="h-5 w-5" />,
    description: '재정 데이터 입력',
    gradient: 'from-blue-500 to-cyan-500'
  },
  {
    id: 'list',
    label: '목록',
    icon: <FaList className="h-5 w-5" />,
    description: '전체 재정 내역',
    gradient: 'from-green-500 to-emerald-500'
  },
  {
    id: 'report',
    label: '보고서',
    icon: <FaChartBar className="h-5 w-5" />,
    description: '월별/연간 분석',
    gradient: 'from-purple-500 to-pink-500'
  },
  {
    id: 'manage',
    label: '관리',
    icon: <FaCog className="h-5 w-5" />,
    description: '잔고 및 이월금',
    gradient: 'from-orange-500 to-red-500'
  }
];

export const FinancePage: React.FC = () => {
  const [activeTab, setActiveTab] = useState<TabType>('input');
  const [showInputModal, setShowInputModal] = useState(false);
  const [selectedDate, setSelectedDate] = useState(new Date().toISOString().split('T')[0]);
  const [reportView, setReportView] = useState<'monthly' | 'annual'>('monthly');

  const handleSuccess = () => {
    console.log('재정 데이터 입력 완료');
  };

  const activeTabConfig = tabs.find(t => t.id === activeTab)!;

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 via-blue-50/30 to-purple-50/30 dark:from-gray-900 dark:via-gray-900 dark:to-gray-800">
      <div className="max-w-7xl mx-auto px-4 py-6">

        {/* 🌟 프리미엄 헤더 */}
        <div className="mb-6">
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
            <div className="flex items-center">
              <div className="p-3 bg-gradient-to-br from-blue-500 to-purple-600 rounded-2xl shadow-lg">
                <FaWallet className="h-8 w-8 text-white" />
              </div>
              <div className="ml-4">
                <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
                  재정관리
                </h1>
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  {activeTabConfig.description}
                </p>
              </div>
            </div>

            {/* 빠른 액션 */}
            <div className="flex flex-wrap items-center gap-3">
              <div className="flex min-w-0 items-center bg-white dark:bg-gray-800 rounded-xl px-3 py-2 shadow-sm border border-gray-200 dark:border-gray-700">
                <FaCalendarAlt className="h-4 w-4 text-gray-400 mr-2" />
                <label htmlFor="finance-entry-date" className="mr-2 whitespace-nowrap text-xs text-gray-600 dark:text-gray-300">입력 날짜</label>
                <input
                  id="finance-entry-date"
                  type="date"
                  value={selectedDate}
                  onChange={(e) => setSelectedDate(e.target.value)}
                  className="bg-transparent border-none focus:ring-0 text-sm text-gray-700 dark:text-gray-200"
                />
              </div>

              <button
                onClick={() => setShowInputModal(true)}
                className="inline-flex items-center px-5 py-2.5 bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 text-white font-medium rounded-xl transition-all shadow-lg hover:shadow-xl hover:scale-105"
              >
                <FaPlus className="h-4 w-4 mr-2" />
                재정 입력
              </button>
            </div>
          </div>
        </div>

        {/* 🎨 프리미엄 탭 네비게이션 */}
        <div className="mb-6">
          <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-lg p-2 border border-gray-200/50 dark:border-gray-700/50">
            <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
              {tabs.map((tab) => (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  aria-pressed={activeTab === tab.id}
                  className={`
                    relative flex flex-col items-center justify-center py-3 px-2 rounded-xl transition-colors duration-200
                    ${activeTab === tab.id
                      ? `bg-gradient-to-r ${tab.gradient} text-white shadow-sm`
                      : 'text-gray-500 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700/50'
                    }
                  `}
                >
                  <div className={`
                    p-2 rounded-lg mb-2 transition-all
                    ${activeTab === tab.id
                      ? 'bg-white/20'
                      : 'bg-gray-100 dark:bg-gray-700'
                    }
                  `}>
                    {tab.icon}
                  </div>
                  <span className="font-semibold text-sm">{tab.label}</span>
                  <span className={`text-xs mt-0.5 ${activeTab === tab.id ? 'text-white/80' : 'text-gray-400'}`}>
                    {tab.description}
                  </span>

                  {/* 활성 표시 인디케이터 */}
                  {activeTab === tab.id && (
                    <div className="absolute bottom-1 left-1/2 transform -translate-x-1/2 w-8 h-1 bg-white/40 rounded-full" />
                  )}
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* 📦 탭 컨텐츠 */}
        <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-lg border border-gray-200/50 dark:border-gray-700/50 overflow-hidden">

          {activeTab === 'input' && <div className="p-4 pb-0 sm:p-6 sm:pb-0">
            <FinanceOverview />
          </div>}

          {/* 입력 탭 */}
          {activeTab === 'input' && (
            <div className="px-6 pb-6 pt-3">
              <div className="rounded-xl border border-gray-200 bg-gray-50/70 p-5 dark:border-gray-700 dark:bg-gray-900/30 sm:p-6">
                <div className="flex flex-col gap-5 sm:flex-row sm:items-center sm:justify-between">
                  <div className="flex items-start gap-4">
                    <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-lg bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300">
                      <FaPlus className="h-5 w-5" />
                    </div>
                    <div>
                      <h2 className="text-lg font-semibold text-gray-900 dark:text-white">재정 내역 입력</h2>
                      <p className="mt-1 text-sm text-gray-600 dark:text-gray-400">헌금과 지출을 기록하고 승인 요청을 시작합니다.</p>
                    </div>
                  </div>
                </div>

                <div className="mt-5 grid grid-cols-1 divide-y divide-gray-200 border-t border-gray-200 dark:divide-gray-700 dark:border-gray-700 lg:grid-cols-3 lg:divide-x lg:divide-y-0">
                  <div className="py-3 lg:pr-4">
                    <h3 className="text-sm font-semibold text-gray-800 dark:text-gray-200">십일조·헌금</h3>
                    <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">성도별 헌금 내역을 기록합니다.</p>
                  </div>
                  <div className="py-3 lg:px-4">
                    <h3 className="text-sm font-semibold text-gray-800 dark:text-gray-200">감사헌금</h3>
                    <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">항목별 감사헌금을 관리합니다.</p>
                  </div>
                  <div className="py-3 lg:pl-4">
                    <h3 className="text-sm font-semibold text-gray-800 dark:text-gray-200">지출</h3>
                    <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">분류와 결제 수단을 함께 기록합니다.</p>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* 목록 탭 */}
          {activeTab === 'list' && (
            <div className="p-3 sm:p-6">
              <FinanceList />
            </div>
          )}

          {/* 보고서 탭 */}
          {activeTab === 'report' && (
            <div className="p-3 sm:p-6">
              {/* 보고서 타입 토글 */}
              <div className="flex items-center justify-center mb-6">
                <div className="inline-flex max-w-full bg-gray-100 dark:bg-gray-700 rounded-xl p-1">
                  <button
                    onClick={() => setReportView('monthly')}
                    aria-pressed={reportView === 'monthly'}
                    className={`px-3 sm:px-6 py-2.5 text-sm sm:text-base rounded-lg font-medium transition-all ${reportView === 'monthly'
                      ? 'bg-white dark:bg-gray-600 text-purple-600 dark:text-purple-400 shadow'
                      : 'text-gray-500 dark:text-gray-400 hover:text-gray-700'
                      }`}
                  >
                    <FaFileAlt className="inline mr-2" />
                    월별 보고서
                  </button>
                  <button
                    onClick={() => setReportView('annual')}
                    aria-pressed={reportView === 'annual'}
                    className={`px-3 sm:px-6 py-2.5 text-sm sm:text-base rounded-lg font-medium transition-all ${reportView === 'annual'
                      ? 'bg-white dark:bg-gray-600 text-purple-600 dark:text-purple-400 shadow'
                      : 'text-gray-500 dark:text-gray-400 hover:text-gray-700'
                      }`}
                  >
                    <FaChartBar className="inline mr-2" />
                    연간 내역
                  </button>
                </div>
              </div>

              {reportView === 'monthly' ? <MonthlyReport /> : <AnnualFinanceView />}
            </div>
          )}

          {/* 관리 탭 */}
          {activeTab === 'manage' && (
            <div className="p-3 sm:p-6">
              {/* 관리 타입 카드 - 동일 높이 */}
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 items-stretch">
                <div className="flex flex-col">
                  <div className="flex items-center mb-4">
                    <div className="p-2 bg-blue-100 dark:bg-blue-900/30 rounded-lg">
                      <FaUniversity className="h-5 w-5 text-blue-600 dark:text-blue-400" />
                    </div>
                    <h3 className="ml-3 text-lg font-semibold text-gray-900 dark:text-white">은행 잔고 관리</h3>
                  </div>
                  <div className="flex-1">
                    <BankBalanceManager />
                  </div>
                </div>

                <div className="flex flex-col">
                  <div className="flex items-center mb-4">
                    <div className="p-2 bg-green-100 dark:bg-green-900/30 rounded-lg">
                      <FaPiggyBank className="h-5 w-5 text-green-600 dark:text-green-400" />
                    </div>
                    <h3 className="ml-3 text-lg font-semibold text-gray-900 dark:text-white">연간 이월금 관리</h3>
                  </div>
                  <div className="flex-1">
                    <CarryoverFundsManager />
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* 재정 입력 모달 */}
      <FinanceInputModal
        isOpen={showInputModal}
        onClose={() => setShowInputModal(false)}
        selectedDate={selectedDate}
        onSuccess={handleSuccess}
      />
    </div>
  );
};
